import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Layout from './Layout';
import AbsenceService from '../services/AbsenceService';

const PublicHolidaysPage = () => {
    const [holidays, setHolidays] = useState([]);
    const [year, setYear] = useState(new Date().getFullYear());
    const [form, setForm] = useState({ date: '', name: '' });
    const [saving, setSaving] = useState(false);

    const fetchHolidays = async (y) => {
        try {
            const data = await AbsenceService.getHolidays(y);
            setHolidays(data || []);
        } catch { /* silently ignore */ }
    };

    useEffect(() => { fetchHolidays(year); }, [year]);

    const handleAdd = async () => {
        if (!form.date || !form.name.trim()) return;
        setSaving(true);
        try {
            await AbsenceService.createHoliday({ date: form.date, name: form.name.trim() });
            setForm({ date: '', name: '' });
            await fetchHolidays(year);
        } catch { /* silently ignore */ }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        try {
            await AbsenceService.deleteHoliday(id);
            setHolidays(h => h.filter(x => x.id !== id));
        } catch { /* silently ignore */ }
    };

    return (
        <Layout>
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#1a2332' }}>
                    Public Holidays
                </Typography>

                <Box sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden', maxWidth: 760 }}>
                    {/* Year selector + add form */}
                    <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                                size="small" variant="outlined"
                                onClick={() => setYear(y => y - 1)}
                                sx={{ minWidth: 32, px: 1, borderColor: '#E2E8F0', color: '#64748B' }}
                            >‹</Button>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A', minWidth: 50, textAlign: 'center' }}>
                                {year}
                            </Typography>
                            <Button
                                size="small" variant="outlined"
                                onClick={() => setYear(y => y + 1)}
                                sx={{ minWidth: 32, px: 1, borderColor: '#E2E8F0', color: '#64748B' }}
                            >›</Button>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5, flex: 1, flexWrap: 'wrap', ml: 'auto' }}>
                            <TextField
                                type="date" size="small" label="Date" InputLabelProps={{ shrink: true }}
                                value={form.date}
                                onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                                sx={{ minWidth: 160 }}
                            />
                            <TextField
                                size="small" label="Holiday name" placeholder="e.g. New Year's Day"
                                value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                sx={{ minWidth: 220 }}
                            />
                            <Button
                                variant="contained" size="small"
                                disabled={!form.date || !form.name.trim() || saving}
                                onClick={handleAdd}
                                startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                                sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', px: 2, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}
                            >
                                Add
                            </Button>
                        </Box>
                    </Box>

                    {/* Holidays list */}
                    {holidays.length === 0 ? (
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>No public holidays for {year}</Typography>
                        </Box>
                    ) : (
                        <Box>
                            {holidays.map((h, i) => (
                                <Box
                                    key={h.id}
                                    sx={{
                                        display: 'flex', alignItems: 'center', px: 3, py: 1.5,
                                        borderBottom: i < holidays.length - 1 ? '1px solid #F8FAFC' : 'none',
                                        '&:hover': { bgcolor: '#F8FAFC' },
                                        '&:hover .del-btn': { opacity: 1 },
                                        '& .del-btn': { opacity: 0, transition: 'opacity 0.15s' },
                                    }}
                                >
                                    <Typography sx={{ fontSize: 13, color: '#64748B', width: 120, flexShrink: 0 }}>
                                        {new Date(h.date + 'T00:00:00').toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A', flex: 1 }}>
                                        {h.name}
                                    </Typography>
                                    <Box className="del-btn">
                                        <Button
                                            size="small"
                                            onClick={() => handleDelete(h.id)}
                                            sx={{ color: '#EF4444', textTransform: 'none', fontSize: 12, minWidth: 0, px: 1 }}
                                        >
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                        </Button>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </Layout>
    );
};

export default PublicHolidaysPage;
